import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  Button,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CheckMarkCircle } from "../../../icons/CheckMarkCircle.svg";
import { IphoneIconPreviewCanvas } from "../IphoneIconPreviewCanvas";
import { useCurrentWorkspace } from "../../../hooks/useCurrentWorkspace";
import placeholderIcon from "../../../assets/demo-appicon.png";
import { BrandedAppLogoSwitchContent } from "./BrandedAppLogoSwitchContent";
import { useRequestBrandedApp } from "../../../hooks/useRequestBrandedApp";
import { Mail } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },
  features: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginBottom: theme.spacing(4.5),
    marginTop: theme.spacing(3),
  },
  feature: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3.5),
    },
    [theme.breakpoints.up("md")]: {
      "&:not(:last-child)": {
        marginBottom: theme.spacing(3.5),
      },
    },
  },

  check: {
    marginRight: theme.spacing(1.25),

    "& svg": {
      width: 30,
      height: 30,
      color: theme.palette.primary.main,
    },
  },

  ctaButton: {
    marginTop: theme.spacing(4),
    fontWeight: 700,
    fontSize: 18,
    padding: theme.spacing(1.5, 4),
  },

  a: {
    color: theme.palette.primary.main,

    "&:hover": {
      color: theme.palette.primary.light,
    },
  },

  requestButton: {
    display: "flex",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(4),
    height: theme.spacing(8),
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    padding: theme.spacing(1, 2),

    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

export function BrandedAppAdvertisingContent() {
  const s = useStyles();

  const { logoUrl, name } = useCurrentWorkspace();
  const { requestBrandedApp, isPending } = useRequestBrandedApp();

  const features = [
    "Your own app in the Apple App Store and Google Play Store",
    "Custom branding: logo, colors, and app name",
    "No mention of Stridist inside your client app",
    "Stand out from competitors and boost client loyalty",
    "No effort is required to migrate your existing and future clients",
    "Branded email and push notifications",
  ];

  return (
    <Box className={s.header}>
      <Box className={s.titleBox}>
        <Typography className={s.title}>Branded apps are here!</Typography>
      </Box>
      <Typography className={s.text} sx={{ marginBottom: 2 }}>
        Elevate your coaching business with a custom-branded mobile app for your
        clients. Deliver a seamless, professional experience and keep your brand
        front and center. Branded Apps are separately billable. Contact support
        at{" "}
        <a className={s.a} href="mailto:accounts@stridist.com">
          accounts@stridist.com
        </a>{" "}
        to know more.
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 4, md: 20 },
          alignItems: "center",
        }}
      >
        <IphoneIconPreviewCanvas
          iconUrl={logoUrl || placeholderIcon}
          appName={name || "Your App"}
          previewTitle="Your Branded App"
        />

        <BrandedAppLogoSwitchContent />
      </Box>
      <List className={s.features}>
        {features.map((text, idx) => (
          <ListItem key={idx} className={s.feature}>
            <ListItemIcon className={s.check}>
              <CheckMarkCircle />
            </ListItemIcon>
            <Typography className={s.text}>{text}</Typography>
          </ListItem>
        ))}
      </List>
      <Button
        className={s.requestButton}
        variant="contained"
        size="large"
        onClick={requestBrandedApp}
        disabled={isPending}
        fullWidth
      >
        Request Branded App <Mail size={30} />
      </Button>
    </Box>
  );
}
